import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown, { ArrowIcon, SortArrowIcon, SortIcon } from "./Dropdown";
import VolatilityFilter from "./VolatilityFilter";
import { useDispatch, useSelector } from "react-redux";
import PaylinesFilter from "./PaylinesFilter";
import { selectMultiplierChanged, selectPaylinesChanged, selectSortIsChanged } from "../../features/catalog/catalogSelectors";
import Checkbox from "./Checkbox";
import MultiplierFilter from "./MultiplierFilter";
import { setSortBy, setSortDirection } from "../../features/catalog/catalogSlice";
import { SortByItem } from "./SortBy";
import MobilePopup, { MobilePopupDropdown } from "./MobilePopup";
import { FeatureTag } from "./FeatureTags";

const TagsPopup = ({
  onCloseCallback
}) => {
  const { t, i18n } = useTranslation();
  const featureTags = useSelector((state) => state.catalog.featureTags);

  return (
    <MobilePopup title={t('tags')} onCloseCallback={onCloseCallback}>
      <div className="feature-tags feature-tags--mobile">
        <div className="feature-tags__list">
          {featureTags.map((tag) => <FeatureTag key={tag.id} tag={tag} />)}
        </div>
      </div>
    </MobilePopup>
  );
};

const FiltersPopup = ({
  onCloseCallback
}) => {
  const [openedDropdown, setOpenedDropdown] = useState(null);
  const isPaylinesChanged = useSelector(selectPaylinesChanged);
  const isMultiplierChanged = useSelector(selectMultiplierChanged);
  const volatilityCount = useSelector((state) => state.catalog.selectedVolatilityOptions.length);

  const handleDropdownToggle = useCallback((dropdown) => {
    setOpenedDropdown(openedDropdown === dropdown ? null : dropdown);
  }, [openedDropdown]);

  const volatilityDropdownIcon = useMemo(() => {
    if (openedDropdown === 'volatility' || !volatilityCount) return null;

    return (
      <span className="filters-count-icon">{volatilityCount}</span>
    );
  }, [volatilityCount, openedDropdown]);

  const paylinesDropdownIcon = useMemo(() => {
    if (openedDropdown === 'paylines' || !isPaylinesChanged) return null;

    return (
      <Checkbox isSelected={true} readOnly={true} />
    );
  }, [isPaylinesChanged, openedDropdown]);

  const multiplierDropdownIcon = useMemo(() => {
    if (openedDropdown === 'multipler' || !isMultiplierChanged) return null;

    return (
      <Checkbox isSelected={true} readOnly={true} />
    );
  }, [isMultiplierChanged, openedDropdown]);

  return (
    <MobilePopup title="Filter By" onCloseCallback={onCloseCallback}>
      <MobilePopupDropdown
        title="Volatility"
        isOpen={openedDropdown === 'volatility'}
        onToggle={() => handleDropdownToggle('volatility')}
        iconSlot={volatilityDropdownIcon}
      >
        <VolatilityFilter />
      </MobilePopupDropdown>
      <MobilePopupDropdown
        title="Paylines"
        isOpen={openedDropdown === 'paylines'}
        onToggle={() => handleDropdownToggle('paylines')}
        iconSlot={paylinesDropdownIcon}
      >
        <PaylinesFilter />
      </MobilePopupDropdown>
      <MobilePopupDropdown
        title="Multipler"
        isOpen={openedDropdown === 'multipler'}
        onToggle={() => handleDropdownToggle('multipler')}
        iconSlot={multiplierDropdownIcon}
      >
        <MultiplierFilter />
      </MobilePopupDropdown>
    </MobilePopup>
  )
};

const SortByPopup = ({
  onCloseCallback
}) => {
  const dispatch = useDispatch();
  const sortBy = useSelector((state) => state.catalog.sortBy);
  const sortDirection = useSelector((state) => state.catalog.sortDirection);

  const handleSortByClick = useCallback((sortBy, sortDirection) => {
    dispatch(setSortBy(sortBy));
    dispatch(setSortDirection(sortDirection));
  }, [dispatch]);

  return (
    <MobilePopup title="Sort By" onCloseCallback={onCloseCallback}>
      <div className="sort-collection">
        <span className="sort-collection__title h-padding">Release Date</span>
        <SortByItem
          title="Recent → Oldest"
          onClick={() => handleSortByClick('releaseDate', 'desc')}
          isSelected={sortBy === 'releaseDate' && sortDirection === 'desc'}
        />
        <SortByItem
          title="Oldest → Recent"
          onClick={() => handleSortByClick('releaseDate', 'asc')}
          isSelected={sortBy === 'releaseDate' && sortDirection === 'asc'}
        />
      </div>
      <div className="sort-collection">
        <span className="sort-collection__title h-padding">Volatility</span>
        <SortByItem
          title="High → Low"
          onClick={() => handleSortByClick('volatility', 'desc')}
          isSelected={sortBy === 'volatility' && sortDirection === 'desc'}
        />
        <SortByItem
          title="Low → High"
          onClick={() => handleSortByClick('volatility', 'asc')}
          isSelected={sortBy === 'volatility' && sortDirection === 'asc'}
        />
      </div>
    </MobilePopup>
  )
};

const MobileFilters = () => {
  const { t, i18n } = useTranslation();
  const [tagsPopupIsOpened, setTagsPopupIsOpened] = useState(false);
  // const [filterPopupIsOpened, setFilterPopupIsOpened] = useState(false);
  // const [sortingPopupIsOpened, setSortingPopupIsOpened] = useState(false);
  // const sortIsChanged = useSelector(selectSortIsChanged);
  // const sortBy = useSelector((state) => state.catalog.sortBy);
  // const sortDirection = useSelector((state) => state.catalog.sortDirection);
  const selectedTagsCount = useSelector((state) => state.catalog.selectedFeatureTags.length);

  const openTagsPopup = useCallback(() => {
    document.body.classList.add('body-overflow');
    setTagsPopupIsOpened(true);
  }, []);

  const closeTagsPopup = useCallback(() => {
    setTagsPopupIsOpened(false);
    document.body.classList.remove('body-overflow');
  }, []);

  // const openFiltersPopup = useCallback(() => {
  //   document.body.classList.add('body-overflow');
  //   setFilterPopupIsOpened(true);
  // }, []);

  // const closeFiltersPopup = useCallback(() => {
  //   setFilterPopupIsOpened(false);
  //   document.body.classList.remove('body-overflow');
  // }, []);

  // const openSortingPopup = useCallback(() => {
  //   document.body.classList.add('body-overflow');
  //   setSortingPopupIsOpened(true);
  // }, []);

  // const closeSortingPopup = useCallback(() => {
  //   setSortingPopupIsOpened(false);
  //   document.body.classList.remove('body-overflow');
  // }, []);

  // const sortByDropdownTitle = useMemo(() => {
  //   if (sortIsChanged) {
  //     return (sortBy === "releaseDate" ? "Release Date" : "Volatility");
  //   }

  //   return "Sort By";
  // }, [sortIsChanged, sortBy]);

  // const sortByDropdownIcon = useMemo(() => {
  //   if (sortIsChanged) {
  //     return <SortArrowIcon isAsc={sortDirection === 'asc'} />
  //   }

  //   return <SortIcon />
  // }, [sortIsChanged, sortDirection]);

  const tagsButtonIcon = useMemo(() => {
    if (!selectedTagsCount) return <ArrowIcon />;

    return (
      <span className="filters-count-icon">{selectedTagsCount}</span>
    );
  }, [selectedTagsCount]);

  return (
    <div className="mobile-filters">
      <Dropdown title={t('tags')} iconSlot={tagsButtonIcon} onClick={openTagsPopup} />
      {/* <Dropdown title="Filter By" iconSlot={<ArrowIcon />} onClick={openFiltersPopup} />
      <Dropdown title={sortByDropdownTitle} iconSlot={sortByDropdownIcon} onClick={openSortingPopup} /> */}
      {tagsPopupIsOpened && <TagsPopup onCloseCallback={closeTagsPopup} />}
      {/* {filterPopupIsOpened && <FiltersPopup onCloseCallback={closeFiltersPopup} />}
      {sortingPopupIsOpened && <SortByPopup onCloseCallback={closeSortingPopup} />} */}
    </div>
  )
}

export default MobileFilters;